import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '@/App';
import { ShoppingCart, Package, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getProducts } from '@/data/products';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cfc143ec-270d-4208-9592-8eae45ed356e/artifacts/4ltgv78b_Untitled%20design-2.png';
const HERO_BG = 'https://images.unsplash.com/photo-1758630737361-ca7532fb5e7f';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const { cart } = useContext(CartContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Load products from local data
    const products = getProducts();
    setFeaturedProducts(products.slice(0, 6));
  }, []);

  return (
    <div className="app-container" data-testid="home-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="logo-container" data-testid="home-link">
            <img src={LOGO_URL} alt="Om Jishnu Logo" className="logo" />
            <span className="brand-name">OM JISHNU</span>
          </Link>
          <div className="nav-links">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/products" className="nav-link" data-testid="nav-products">Products</Link>
            <Link to="/admin" className="nav-link" data-testid="nav-admin">Admin</Link>
            <Link to="/checkout" className="cart-badge" data-testid="nav-cart">
              <ShoppingCart size={24} />
              {cart.length > 0 && (
                <span className="cart-count" data-testid="cart-count">{cart.length}</span>
              )}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="hero-section"
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">Quality Printing Solutions</h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Premium printers, ink cartridges, and supplies for all your printing needs
          </p>
          <Button
            className="btn-primary"
            onClick={() => navigate('/products')}
            data-testid="shop-now-btn"
          >
            Shop Now
          </Button>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '4rem 2rem', background: 'white' }} data-testid="features-section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            <div style={{ textAlign: 'center', padding: '2rem' }} data-testid="feature-quality">
              <Package size={48} style={{ color: '#A44A66', margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Quality Products</h3>
              <p style={{ color: '#666' }}>Authentic printers and ink from trusted brands</p>
            </div>
            <div style={{ textAlign: 'center', padding: '2rem' }} data-testid="feature-prices">
              <TrendingUp size={48} style={{ color: '#A44A66', margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Best Prices</h3>
              <p style={{ color: '#666' }}>Competitive pricing on all products</p>
            </div>
            <div style={{ textAlign: 'center', padding: '2rem' }} data-testid="feature-delivery">
              <ShoppingCart size={48} style={{ color: '#A44A66', margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Fast Delivery</h3>
              <p style={{ color: '#666' }}>Quick and reliable shipping to your door</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section style={{ padding: '4rem 2rem', background: '#f8f8f8' }} data-testid="featured-products-section">
        <h2 className="section-title" data-testid="featured-title">Featured Products</h2>
        <div className="product-grid">
          {featuredProducts.map((product) => (
            <div
              key={product.id}
              className="product-card"
              onClick={() => navigate(`/product/${product.id}`)}
              data-testid={`product-card-${product.id}`}
            >
              <img src={product.image_url} alt={product.name} className="product-image" />
              <div className="product-info">
                <div className="product-category" data-testid="product-category">{product.category}</div>
                <h3 className="product-name" data-testid="product-name">{product.name}</h3>
                <p className="product-brand" data-testid="product-brand">{product.brand}</p>
                <div className="product-price" data-testid="product-price">₹{product.price.toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <Button
            className="btn-secondary"
            onClick={() => navigate('/products')}
            data-testid="view-all-btn"
          >
            View All Products
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: '#1a1a1a', color: 'white', padding: '3rem 2rem', textAlign: 'center' }} data-testid="footer">
        <div className="container">
          <img src={LOGO_URL} alt="Om Jishnu" style={{ height: '60px', marginBottom: '1rem' }} />
          <p style={{ marginBottom: '0.5rem', fontSize: '1.2rem', fontWeight: '600' }}>OM JISHNU</p>
          <p style={{ color: '#999', marginBottom: '1.5rem' }}>Your trusted partner for printing solutions</p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>© 2025 Om Jishnu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;